import './rw-wonen-hero.scss';
import gsap from 'gsap';
import { Observer } from 'gsap/Observer';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin( Observer, CustomEase );
CustomEase.create( 'slideshow-wipe', '0.6, 0.08, 0.02, 0.99' );

function initRwWonenHero( el ) {
	const slides      = Array.from( el.querySelectorAll( '[data-slideshow="slide"]' ) );
	const inner       = Array.from( el.querySelectorAll( '[data-slideshow="parallax"]' ) );
	const thumbs      = Array.from( el.querySelectorAll( '[data-slideshow="thumb"]' ) );
	const prevBtn     = el.querySelector( '[data-slideshow="prev"]' );
	const nextBtn     = el.querySelector( '[data-slideshow="next"]' );
	const overlay     = el.querySelector( '.p-rwWonenHero__overlay' );
	const heroContent = el.querySelector( '.p-rwWonenHero__content' );

	if ( slides.length <= 1 ) {
		if ( prevBtn ) prevBtn.hidden = true;
		if ( nextBtn ) nextBtn.hidden = true;
		return null;
	}

	let current           = 0;
	const length          = slides.length;
	let animating         = false;
	let observer;
	let fullscreenTl      = null;
	let scrollHandler     = null;
	let isFullscreen      = false;
	const animDur         = 0.9;
	const maxVisibleThumbs = 4;
	const isDesktop       = () => window.matchMedia( '(min-width: 768px)' ).matches;

	slides[ current ].classList.add( 'is--current' );
	if ( thumbs.length ) {
		thumbs[ current ].classList.add( 'is--current' );
		updateVisibleThumbs();
	}

	function updateVisibleThumbs() {
		if ( ! thumbs.length ) return;

		if ( length <= maxVisibleThumbs ) {
			thumbs.forEach( ( thumb ) => {
				gsap.to( thumb, { opacity: 1, scale: 1, width: '4.375rem', duration: 0.3, display: 'block' } );
			} );
			return;
		}

		let startIndex, endIndex;
		if ( current < 2 ) {
			startIndex = 0;
			endIndex   = maxVisibleThumbs - 1;
		} else if ( current >= length - 2 ) {
			startIndex = length - maxVisibleThumbs;
			endIndex   = length - 1;
		} else {
			startIndex = current - 1;
			endIndex   = current + 2;
		}

		thumbs.forEach( ( thumb, index ) => {
			if ( index >= startIndex && index <= endIndex ) {
				gsap.to( thumb, { opacity: 1, scale: 1, width: '4.375rem', duration: 0.3, display: 'block', ease: 'power2.out' } );
			} else {
				gsap.to( thumb, {
					opacity: 0, scale: 0.8, width: 0, duration: 0.3, ease: 'power2.in',
					onComplete() { thumb.style.display = 'none'; },
				} );
			}
		} );
	}

	function enterFullscreen() {
		if ( isFullscreen || ! isDesktop() ) return;
		isFullscreen = true;
		if ( fullscreenTl ) fullscreenTl.kill();
		fullscreenTl = gsap.timeline( { defaults: { ease: 'power2.inOut' } } );
		if ( overlay ) fullscreenTl.to( overlay, { opacity: 0, duration: 0.5, pointerEvents: 'none' }, 0 );
		if ( heroContent ) fullscreenTl.to( heroContent, { opacity: 0, y: 30, duration: 0.5, pointerEvents: 'none' }, 0 );
	}

	function exitFullscreen() {
		if ( ! isFullscreen ) return;
		isFullscreen = false;
		if ( fullscreenTl ) fullscreenTl.kill();
		fullscreenTl = gsap.timeline( { defaults: { ease: 'power2.inOut' } } );
		if ( overlay ) fullscreenTl.to( overlay, { opacity: 1, duration: 0.5, pointerEvents: 'auto' }, 0 );
		if ( heroContent ) fullscreenTl.to( heroContent, { opacity: 1, y: 0, duration: 0.5, pointerEvents: 'auto' }, 0 );
	}

	function navigate( direction, targetIndex = null ) {
		if ( animating ) return;
		animating = true;
		observer.disable();

		const previous = current;
		if ( targetIndex !== null ) {
			current = targetIndex;
		} else {
			current = direction === 1
				? ( current < length - 1 ? current + 1 : 0 )
				: ( current > 0 ? current - 1 : length - 1 );
		}

		const fromSlide = slides[ previous ];
		const fromInner = inner[ previous ];
		const toSlide   = slides[ current ];
		const toInner   = inner[ current ];
		const dir       = targetIndex !== null ? ( current > previous ? 1 : -1 ) : direction;

		gsap.timeline( {
			defaults: { duration: animDur, ease: 'slideshow-wipe' },
			onStart() {
				toSlide.classList.add( 'is--current' );
				if ( thumbs.length ) {
					thumbs[ previous ].classList.remove( 'is--current' );
					thumbs[ current ].classList.add( 'is--current' );
				}
			},
			onComplete() {
				fromSlide.classList.remove( 'is--current' );
				if ( thumbs.length ) updateVisibleThumbs();
				animating = false;
				setTimeout( () => observer.enable(), animDur * 1000 );
			},
		} )
			.to( fromSlide, { xPercent: -dir * 100 }, 0 )
			.to( fromInner, { xPercent: dir * 50 }, 0 )
			.fromTo( toSlide, { xPercent: dir * 100 }, { xPercent: 0 }, 0 )
			.fromTo( toInner, { xPercent: -dir * 50 }, { xPercent: 0 }, 0 );
	}

	const thumbClickHandlers = [];
	thumbs.forEach( ( thumb, index ) => {
		const handler = () => {
			if ( index === current || animating ) return;
			enterFullscreen();
			navigate( index > current ? 1 : -1, index );
		};
		thumb.addEventListener( 'click', handler );
		thumbClickHandlers.push( { thumb, handler } );
	} );

	if ( prevBtn ) {
		prevBtn.addEventListener( 'click', () => {
			enterFullscreen();
			navigate( -1 );
		} );
	}
	if ( nextBtn ) {
		nextBtn.addEventListener( 'click', () => {
			enterFullscreen();
			navigate( 1 );
		} );
	}

	observer = Observer.create( {
		target: el,
		type: 'touch,pointer',
		onLeft() {
			if ( ! animating ) { enterFullscreen(); navigate( 1 ); }
		},
		onRight() {
			if ( ! animating ) { enterFullscreen(); navigate( -1 ); }
		},
		tolerance: 10,
	} );

	let lastScrollY = window.scrollY;
	scrollHandler = () => {
		const y = window.scrollY;
		if ( isFullscreen && Math.abs( y - lastScrollY ) > 5 ) {
			exitFullscreen();
		}
		lastScrollY = y;
	};
	window.addEventListener( 'scroll', scrollHandler, { passive: true } );

	return function destroy() {
		if ( observer ) observer.kill();
		if ( fullscreenTl ) fullscreenTl.kill();
		if ( scrollHandler ) window.removeEventListener( 'scroll', scrollHandler );
		thumbClickHandlers.forEach( ( { thumb, handler } ) => thumb.removeEventListener( 'click', handler ) );
	};
}

const init = () => {
	document.querySelectorAll( '.p-rwWonenHero' ).forEach( ( el ) => {
		const cleanup = initRwWonenHero( el );
		if ( cleanup ) el._rwHeroCleanup = cleanup;
	} );
};

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
